import React, { useState } from 'react';
import api from '../services/api';

const MAX_PROMPT_LENGTH = 500;
const MAX_NOTE_LENGTH = 300;

/**
 * FriendSuggestionModal - Modal for suggesting a custom reflection prompt to a friend.
 * Props:
 *   friend     - { id, username } object for the recipient
 *   onClose    - called when the modal should be dismissed
 *   onSuccess  - called after a successful submission (to refresh suggestion history)
 */
function FriendSuggestionModal({ friend, onClose, onSuccess }) {
  const [promptText, setPromptText] = useState('');
  const [personalNote, setPersonalNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    const trimmedPrompt = promptText.trim();
    if (!trimmedPrompt) {
      setError('Please enter a prompt for your friend.');
      return;
    }
    if (trimmedPrompt.length > MAX_PROMPT_LENGTH) {
      setError(`Prompt must be ${MAX_PROMPT_LENGTH} characters or fewer.`);
      return;
    }

    setError('');
    setSubmitting(true);
    try {
      await api.post('/friend-suggestions', {
        friendUserId: friend.id,
        promptText: trimmedPrompt,
        personalNote: personalNote.trim() || undefined
      });
      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to send suggestion. Please try again.';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div className="modal-content friend-suggestion-modal" onClick={(e) => e.stopPropagation()}>
        <h2>💌 Suggest a Prompt</h2>
        <p className="friend-suggestion-modal-subtitle">
          Write a reflection prompt for <strong>{friend.username}</strong>. It will appear in their <em>From Friends</em> category.
        </p>

        {error && (
          <div className="friend-suggestion-error">{error}</div>
        )}

        <div className="form-group">
          <label htmlFor="fs-prompt-text">
            Prompt <span className="required-star">*</span>
          </label>
          <textarea
            id="fs-prompt-text"
            value={promptText}
            onChange={(e) => setPromptText(e.target.value)}
            placeholder="e.g. What's a moment with me you'd want to remember forever?"
            rows="4"
            maxLength={MAX_PROMPT_LENGTH}
            disabled={submitting}
          />
          <div className="char-count">
            {promptText.length}/{MAX_PROMPT_LENGTH}
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="fs-personal-note">
            Personal note <span className="optional-label">(optional)</span>
          </label>
          <textarea
            id="fs-personal-note"
            value={personalNote}
            onChange={(e) => setPersonalNote(e.target.value)}
            placeholder="e.g. This reminded me of our trip last summer — I'd love to hear your side of it!"
            rows="3"
            maxLength={MAX_NOTE_LENGTH}
            disabled={submitting}
          />
          <div className="char-count">
            {personalNote.length}/{MAX_NOTE_LENGTH}
          </div>
        </div>

        <div className="modal-actions">
          <button className="btn-cancel" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button
            className="btn-save"
            onClick={handleSubmit}
            disabled={submitting || !promptText.trim()}
          >
            {submitting ? 'Sending…' : 'Send Prompt'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default FriendSuggestionModal;
